import { useState, useRef, useEffect } from "react";
import { apiUrl } from "../utils/apiBase";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, User, Shield, Wifi, Server, Activity } from "lucide-react";

// Aggiunta buttonRef e onClose come props
export default function UserMenu({
  dark = true,
  hideHeader = false,
  buttonRef,
  onClose,
}) {
  const { user, logout } = useAuth();
  // Stato interno showMenu solo se non hideHeader
  const [showMenu, setShowMenu] = useState(false);
  const internalButtonRef = useRef(null);
  // Inizializza menuPosition con right: 0 per evitare posizionamenti strani iniziali
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  // Stati per admin
  const [vpnStatus, setVpnStatus] = useState(null);
  const [proxyStatus, setProxyStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch status quando il menu è visibile (controllato da AppContent o stato interno)
  useEffect(() => {
    // Fetch sempre se il componente è montato e user è presente, e il menu è visibile (esternamente o internamente)
    if (user && (showMenu || hideHeader)) {
      fetchStatus();
      // Aggiorna ogni 10 secondi quando il menu è visibile
      const interval = setInterval(fetchStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [user?.role, showMenu, hideHeader]); // Dipende dall'utente e dalla visibilità

  // Fetch status VPN/Proxy per tutti gli utenti
  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem("ktv-token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      
      // 🔧 ELECTRON FIX: In modalità Electron, carica solo VPN status
      const isElectron = typeof window !== 'undefined' && window.ktvEnv && window.ktvEnv.isElectron;
      
      if (isElectron) {
        // Solo VPN status per Electron
        const vpnRes = await fetch(apiUrl("/api/admin/vpn/status"), { headers });
        if (vpnRes.ok) setVpnStatus(await vpnRes.json());
        else setVpnStatus(null);
        setProxyStatus(null); // Non mostrare proxy in Electron
      } else {
        // Entrambi per modalità web
        const [vpnRes, proxyRes] = await Promise.all([
          fetch(apiUrl("/api/admin/vpn/status"), { headers }),
          fetch(apiUrl("/api/admin/proxy/status"), { headers }),
        ]);
        if (vpnRes.ok) setVpnStatus(await vpnRes.json());
        else setVpnStatus(null);
        if (proxyRes.ok) setProxyStatus(await proxyRes.json());
        else setProxyStatus(null);
      }
    } catch (error) {
      setVpnStatus(null);
      setProxyStatus(null);
    }
  };

  const handleRestart = async (type) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      // Ottieni il token dal localStorage
      const token = localStorage.getItem("ktv-token");
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(apiUrl(`/api/admin/${type}/restart`), {
        method: "POST",
        headers,
      });

      if (response.ok) {
        // Aggiorna lo stato dopo 3 secondi
        setTimeout(fetchStatus, 3000);
      } else {
        console.error(`Restart ${type} error:`, await response.text());
      }
    } catch (error) {
      console.error(`Failed to restart ${type}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calcola posizione menu usando il buttonRef passato o quello interno
  useEffect(() => {
    const targetRef = hideHeader ? buttonRef : internalButtonRef;
    // Calcola posizione solo se el ref è disponibile e el menu è visibile (esternamente o internamente)
    if ((showMenu || hideHeader) && targetRef && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        // Calcola right per allineare el bordo destro del menu al bordo destro del bottone
        right: window.innerWidth - rect.right,
      });
    }
  }, [showMenu, hideHeader, buttonRef, internalButtonRef]); // Dipende da tutti i ref, showMenu e hideHeader

  // Rimosso stato e logica menuVisible interna, gestita da AppContent o showMenu
  // const [menuVisible, setMenuVisible] = useState(false);

  // Gestione animazione apertura/chiusura (solo se non hideHeader)
  // useEffect(() => { ... }, [showMenu, menuVisible, hideHeader]);

  if (!user) return null;

  const handleLogout = () => {
    logout();
    // Chiudi el menu dopo el logout chiamando la prop onClose o lo stato interno
    if (onClose) onClose();
    else setShowMenu(false);
  };

  // Determina se mostrare el portale: se hideHeader è true (visibilità controllata da AppContent)
  // O se hideHeader è false E showMenu è true (visibilità controllata internamente)
  const shouldShowPortal = hideHeader || showMenu;

  return (
    <>
      {" "}
      {/* Mantieni el fragment come root element */}
      {/* User Avatar Button (solo se non hideHeader) */}
      {!hideHeader && (
        <button
          ref={internalButtonRef} // Usa el ref interno per questo bottone
          onClick={() => setShowMenu(!showMenu)} // Toggle stato interno
          className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 hover:scale-105 shadow-lg ${
            dark
              ? "bg-gradient-to-br from-purple-500/30 to-pink-500/30 text-purple-200 hover:from-purple-500/40 hover:to-pink-500/40 border border-purple-400/30 hover:border-purple-300/50"
              : "bg-gradient-to-br from-purple-100 to-pink-100 text-purple-700 hover:from-purple-200 hover:to-pink-200 border border-purple-300"
          }`}
          title={`${user.username} (${user.role})`}
        >
          <User size={20} />
        </button>
      )}
      {/* Renderizza el menu usando un portale per garantire el massimo z-index */}
      {shouldShowPortal &&
        createPortal(
          <>
            {" "}
            {/* Mantieni el fragment come root element del portale */}
            {/* Backdrop per chiudere el menu (solo se non hideHeader) */}
            {!hideHeader && (
              <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-200 ${
                  showMenu ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                style={{ zIndex: 2147483646 }}
                onClick={() => {
                  if (onClose) onClose();
                  else setShowMenu(false);
                }}
              />
            )}
            {/* Menu dropdown con gradiente coerente e animazione */}
            <div
              // Applica classi di animazione e visibilità basate su showMenu (se non hideHeader)
              // O sempre visibile se hideHeader è true (visibilità esterna)
              className={`fixed w-72 rounded-2xl shadow-2xl border border-white/10 transition-all duration-200 ${
                !hideHeader &&
                (showMenu
                  ? "opacity-100 translate-y-0 scale-100"
                  : "opacity-0 translate-y-4 scale-95 pointer-events-none")
              }`}
              style={{
                zIndex: 2147483647,
                top: menuPosition.top + "px",
                right: menuPosition.right + "px", // Usa right per posizionamento
                background: "linear-gradient(135deg, #2a0845 0%, #6441a5 100%)",
                color: "#fff",
                backdropFilter: "blur(16px)",
                boxShadow: "0 4px 32px #0008",
                borderRadius: 20,
                minWidth: 320, // Aggiunto min-width qui
                // Se hideHeader è true, el menu è sempre visibile e non animato da classi
                opacity: hideHeader ? 1 : undefined,
                transform: hideHeader ? "translateY(0) scale(1)" : undefined,
                pointerEvents: hideHeader ? "auto" : undefined,
              }}
            >
              {/* Header del menu - sempre visibile per mostrare l'utente collegato */}
              <div
                className={`border-b border-white/10 ${
                  hideHeader ? "p-4" : "p-6"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`rounded-full flex items-center justify-center font-bold bg-gradient-to-br from-purple-400/30 to-pink-400/30 text-purple-100 border border-purple-400/20 shadow-lg ${
                      hideHeader ? "w-10 h-10 text-lg" : "w-14 h-14 text-xl"
                    }`}
                  >
                    <User size={hideHeader ? 20 : 24} />
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-bold text-white mb-1 ${
                        hideHeader ? "text-base" : "text-lg"
                      }`}
                    >
                      {user.username}
                    </div>
                    <div
                      className={`px-3 py-1.5 rounded-full inline-flex items-center gap-2 font-medium ${
                        hideHeader ? "text-xs" : "text-sm"
                      } ${
                        user.role === "admin"
                          ? "bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-200 border border-yellow-400/30"
                          : "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-200 border border-blue-400/30"
                      }`}
                    >
                      {user.role === "admin" ? (
                        <>
                          <Shield size={hideHeader ? 14 : 16} />
                          Amministratore
                        </>
                      ) : (
                        <>
                          <User size={hideHeader ? 14 : 16} />
                          Utente
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Stato VPN/Proxy - integrato, senza box */}
              <div className="p-6 border-b border-white/10 text-sm text-white/90 space-y-6">
                {/* Sezione VPN - sempre visibile */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 font-semibold mb-3 text-purple-200">
                    <Wifi size={18} className="text-purple-300" />
                    Stato VPN
                  </div>
                  {vpnStatus ? (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">IP Address:</span>
                        <span className="font-mono text-white bg-gray-800/50 px-2 py-1 rounded text-sm">
                          {vpnStatus.ip}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Paese:</span>
                        <span className="text-white">
                          {vpnStatus.countryCode} {vpnStatus.country}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Connessione:</span>
                        <span
                          className={`flex items-center gap-2 font-medium ${
                            vpnStatus.status === "connected"
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${
                              vpnStatus.status === "connected"
                                ? "bg-green-400 animate-pulse"
                                : "bg-red-400"
                            }`}
                          ></div>
                          {vpnStatus.status === "connected"
                            ? "Connesso"
                            : "Disconnesso"}
                        </span>
                      </div>
                      {user?.role === "admin" && (
                        <button
                          onClick={() => handleRestart("vpn")}
                          disabled={isLoading}
                          className="w-full mt-3 text-sm px-3 py-2 rounded-lg bg-blue-500/30 text-blue-100 hover:bg-blue-500/50 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Activity size={16} />
                          Riavvia VPN
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-white/50 flex items-center gap-2">
                      <div className="animate-spin w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full"></div>
                      Caricamento...
                    </div>
                  )}
                </div>
                
                {/* Sezione Proxy - solo in modalità web */}
                {!(typeof window !== 'undefined' && window.ktvEnv && window.ktvEnv.isElectron) && (
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <div className="flex items-center gap-2 font-semibold mb-3 text-purple-200">
                      <Server size={18} className="text-purple-300" />
                      Stato Proxy
                    </div>
                    {proxyStatus ? (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Process ID:</span>
                          <span className="font-mono text-white bg-gray-800/50 px-2 py-1 rounded text-sm">
                            {proxyStatus.pid || "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Connessioni:</span>
                          <span className="font-mono text-white">
                            {proxyStatus.activeConnections || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Stato Server:</span>
                          <span
                            className={`flex items-center gap-2 font-medium ${
                              proxyStatus.status === "running"
                                ? "text-green-400"
                                : "text-red-400"
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full ${
                                proxyStatus.status === "running"
                                  ? "bg-green-400 animate-pulse"
                                  : "bg-red-400"
                              }`}
                            ></div>
                            {proxyStatus.status === "running"
                              ? "Attivo"
                              : "Fermo"}
                          </span>
                        </div>
                        {user?.role === "admin" && (
                          <button
                            onClick={() => handleRestart("proxy")}
                            disabled={isLoading}
                            className="w-full mt-3 text-sm px-3 py-2 rounded-lg bg-purple-500/30 text-purple-100 hover:bg-purple-500/50 disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2"
                          >
                            <Activity size={16} />
                            Riavvia Proxy
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-white/50 flex items-center gap-2">
                        <div className="animate-spin w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full"></div>
                        Caricamento...
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Azioni */}
              <div className="p-6">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 rounded-xl flex items-center gap-3 text-red-300 hover:bg-red-500/20 hover:text-red-200 border border-red-500/20 hover:border-red-400/40 bg-red-500/10"
                >
                  <LogOut size={20} />
                  Disconnetti
                </button>
              </div>
            </div>
            {/* Backdrop per chiudere il menu (solo se non hideHeader) */}
            {!hideHeader && (
              <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-200 ${
                  showMenu ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                style={{ zIndex: 2147483646 }}
                onClick={() => {
                  if (onClose) onClose();
                  else setShowMenu(false);
                }}
              />
            )}
          </>,
          document.body
        )}
    </>
  );
}
